// Selection-driven camera glide, and the single-player sim's hand-over to an
// online match (pause + test bridge). Extracted from App.tsx (ROADMAP 7.14);
// the hook order is unchanged.

import type { SceneNode } from '@aod/protocol';
import { useEffect, useRef } from 'react';
import type { GameMode } from '../multiplayer/lobby/shouldConnectMpLobby.js';
import type { Vec3 } from '../render/camera/flight.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { type Selection, useTestBridge } from '../testBridge/useTestBridge.js';
import type { AttackToast } from '../ui/AttackToastStack.js';

/**
 * Selecting a tile, city or unit glides the camera to it only when it is
 * off screen or near the edge (e.g. picked from the unit tray, or clicked
 * at the rim of the view). Clicking something already in plain view leaves
 * the camera alone: re-centring on every click felt jumpy, and following a
 * moving unit would fight the player. Keyed on the selection itself, so
 * re-renders and the selected unit moving never re-fly.
 */
export function useSelectionFlight(
  selected: SceneNode | null,
  selection: Selection,
  flyCameraTo: (position: Vec3, when?: 'always' | 'if-off-screen') => void,
) {
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const selectionKey = selection ? `${selection.kind}:${selection.id}` : null;
  useEffect(() => {
    const node = selectedRef.current;
    if (!selectionKey || !node) return;
    const [x, y, z] = node.transform.position;
    flyCameraTo([x, y, z], 'if-off-screen');
  }, [selectionKey, flyCameraTo]);
}

/**
 * The single-player sim boots with the app and would keep ticking behind an
 * online match: a second full world per browser, and its test bridge would
 * shadow the match's. Pause it, and let the match own `window.__aodTest`.
 */
export function useSinglePlayerBridge(
  sim: SimWorld,
  gameMode: GameMode,
  localPlayerIndex: number,
  selection: Selection,
  setSelection: (s: Selection) => void,
  attackToasts: ReadonlyArray<AttackToast>,
) {
  const inOnlineMatch = gameMode === 'multiplayer-match';
  const pauseSinglePlayerSim = sim.pause;
  useEffect(() => {
    if (inOnlineMatch && sim.status === 'ready') pauseSinglePlayerSim();
  }, [inOnlineMatch, sim.status, pauseSinglePlayerSim]);
  useTestBridge({
    sim,
    localPlayerIndex,
    selection,
    setSelection,
    attackToasts,
    enabled: !inOnlineMatch,
  });
}
